/* eslint-disable @typescript-eslint/camelcase */
import { ClearingsAction, ClearingsActionType } from './actions';
import {
  Loadable,
  LoadableStatus,
  DailyClearingByCard,
  Discounts,
  ClearingSummaryTable,
  ClearingSummary,
  Paginated,
  MonthlyPayments,
  idle
} from '@card/types';
import { ObjectUtils } from '@card/commonlib';

export interface ClearingsState {
  daily: Loadable<ClearingSummaryTable[]>;
  uploadScreen: {
    fileUploaded: boolean;
    error?: string;
    uploading: boolean;
  };
  monthlyPayments: Loadable<Paginated<MonthlyPayments>>;
}

const initialState: ClearingsState = {
  uploadScreen: {
    fileUploaded: false,
    uploading: false
  },
  daily: idle(),
  monthlyPayments: idle()
};

const PROVIDER_MAP: Record<string, string> = {
  argencard: 'others',
  cencosud: 'others',
  cmr: 'others',
  cordobesa: 'others',
  diners: 'diners',
  jcb: 'others',
  lider: 'others',
  maestro: 'mastercard',
  mastercard_debit: 'mastercard',
  mc_debit: 'mastercard',
  ticketnacion: 'others',
  visa_debit: 'visa'
};

const OTHERS_PRIORITY = 1024;

const PROVIDER_PRIORITY: Record<string, number> = {
  visa: 1,
  mastercard: 2,
  amex: 3,
  naranja: 4,
  others: OTHERS_PRIORITY
};

/*
// TODO(dbelay): to use in next clearings PR
const PROVIDER_MAP_DOMINICANA: Record<string, string> = {
  amex: 'others',
  discover: 'others',
  diners: 'others'
};
*/

function mapDiscounts(
  discounts: Pick<
    DailyClearingByCard,
    | 'Clearing::Deduction::SellDiscount'
    | 'Clearing::Deduction::FinancialCost'
    | 'Clearing::Taxes::Perception'
    | 'Clearing::Taxes::Taxation'
    | 'Clearing::Taxes::Withholding'
    | 'Clearing::Deduction::Arancel'
    | 'Clearing::Deduction::Other'
    | 'Clearing::Deduction::AdditionalService'
    | 'Clearing::Deduction::EcommerceService'
  >
): Discounts {
  return {
    sellDiscount: discounts['Clearing::Deduction::SellDiscount'] || 0,
    financialCost: discounts['Clearing::Deduction::FinancialCost'] || 0,
    taxes:
      (+discounts['Clearing::Taxes::Perception'] || 0) +
      (+discounts['Clearing::Taxes::Taxation'] || 0) +
      (+discounts['Clearing::Taxes::Withholding'] || 0),
    arancel: +discounts['Clearing::Deduction::Arancel'] || 0,
    others:
      (+discounts['Clearing::Deduction::Other'] || 0) +
      (+discounts['Clearing::Deduction::AdditionalService'] || 0) +
      (+discounts['Clearing::Deduction::EcommerceService'] || 0)
  };
}

function groupClearingsByProvider(dailyClearing: ClearingSummary): ClearingSummary {
  return Object.keys(dailyClearing).reduce((acc: ClearingSummary, key) => {
    const clearing = dailyClearing[key];
    const provider = PROVIDER_MAP[key] || key;
    if (acc[provider]) {
      acc[provider] = ObjectUtils.sumObjectsByKey(acc[provider], clearing);
    } else {
      acc[provider] = clearing;
    }
    return acc;
  }, {});
}

function fromApi(
  provider: string,
  {
    debit,
    credit,
    credit_with_installments,
    opening_balance,
    closing_balance,
    total_amount,
    total_deduction,
    total_earn,
    total_chargebacks,
    total_credit_financed_by_processor,
    total_credit_financed_by_merchant,
    total_debit,
    total_credit_financed_by_processor_multiple_installments,
    total_credit_financed_by_processor_single_installment,
    negative_adjustment,
    ...discounts
  }: DailyClearingByCard
): ClearingSummaryTable {
  return {
    provider,
    presented: {
      debit: debit || total_debit,
      credit: credit || total_credit_financed_by_processor_single_installment,
      creditWithInstallments:
        credit_with_installments || total_credit_financed_by_processor_multiple_installments,
      creditFinancedByMerchant: total_credit_financed_by_merchant,
      creditFinancedByProcessor: total_credit_financed_by_processor,
      openingBalance: opening_balance,
      closingBalance: closing_balance,
      totalChargebacks: total_chargebacks || negative_adjustment
    },
    totalAmount: total_amount,
    totalDeduction: total_deduction,
    totalEarn: total_earn,
    discountAmounts: mapDiscounts(discounts)
  };
}

function transformClearingToTable(dailyClearing: ClearingSummary): ClearingSummaryTable[] {
  return Object.keys(dailyClearing).map((provider) => {
    return fromApi(provider, dailyClearing[provider]);
  });
}

function sortProvidersByPriority(clearingTables: ClearingSummaryTable[]): ClearingSummaryTable[] {
  return clearingTables.sort(
    (a, b) =>
      (PROVIDER_PRIORITY[a.provider] || OTHERS_PRIORITY) -
      (PROVIDER_PRIORITY[b.provider] || OTHERS_PRIORITY)
  );
}

function formatClearings(dailyClearings: ClearingSummary): ClearingSummaryTable[] {
  dailyClearings = groupClearingsByProvider(dailyClearings);
  const clearingTables = transformClearingToTable(dailyClearings);
  return sortProvidersByPriority(clearingTables);
}

export function clearingsReducer(state = initialState, action: ClearingsAction): ClearingsState {
  switch (action.type) {
    case ClearingsActionType.FetchDailyStarted:
      return {
        ...state,
        daily: { ...state.daily, status: LoadableStatus.Fetching }
      };
    case ClearingsActionType.FetchDailySuccess:
      return {
        ...state,
        daily: { status: LoadableStatus.Fetched, data: formatClearings(action.payload) }
      };
    case ClearingsActionType.FetchDailyError:
      return { ...state, daily: { status: LoadableStatus.Error, error: action.payload } };
    case ClearingsActionType.FetchMonthlyStarted:
      return {
        ...state,
        monthlyPayments: { ...state.monthlyPayments, status: LoadableStatus.Fetching }
      };
    case ClearingsActionType.FetchMonthlySuccess:
      return {
        ...state,
        monthlyPayments: {
          ...state.monthlyPayments,
          status: LoadableStatus.Fetched,
          data: action.payload
        }
      };
    case ClearingsActionType.FetchMonthlyError:
      return {
        ...state,
        monthlyPayments: {
          ...state.monthlyPayments,
          status: LoadableStatus.Error,
          error: action.payload
        }
      };
    case ClearingsActionType.ManualUploadStarted: {
      return {
        ...state,
        uploadScreen: {
          fileUploaded: false,
          error: undefined,
          uploading: true
        }
      };
    }
    case ClearingsActionType.ManualUploadError: {
      return {
        ...state,
        uploadScreen: {
          fileUploaded: false,
          error: action.payload.message,
          uploading: false
        }
      };
    }
    case ClearingsActionType.ManualUploadSuccess: {
      return {
        ...state,
        uploadScreen: {
          fileUploaded: true,
          error: undefined,
          uploading: false
        }
      };
    }
    default:
      return state;
  }
}
